import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';

import type { Nullable } from '@repo/common';
import type { FileResourceReference, UploadedFileResource } from '@repo/ui/api/file-upload';
import { convertToWebp } from '@repo/ui/utils/image/convert-image-to-webp';

import { uploadFile } from '../api/file-upload';

const downloadFile = async (url: string, fileName: Nullable<string>, signal: AbortSignal): Promise<File | null> => {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status}`);
  }
  const blob = await response.blob();
  return new File([blob], fileName || 'originalFile', { type: blob.type });
};

const getLocalFileUrl = async (file: File | null): Promise<string | null> => {
  if (!file) {
    return null;
  }
  const reader = new FileReader();
  return await new Promise((resolve, reject) => {
    reader.onload = e => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve(result);
        return;
      }
      reject(new Error('Failed to read the selected file'));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

async function downloadResource(
  resource: FileResourceReference,
  signal: AbortSignal,
): Promise<{
  downloadedFile: File | null;
  localFileUrl: string | null;
}> {
  const downloadedFile = await downloadFile(resource.relativePath, resource.fileName, signal);
  return { downloadedFile, localFileUrl: await getLocalFileUrl(downloadedFile) };
}

interface FileState {
  sourceKey: string;
  resource: Nullable<FileResourceReference>;
  file: Nullable<File>;
  localFileUrl: string | null;
  loading: boolean;
  replaced: boolean;
}

export interface FileUploadResult {
  resource: UploadedFileResource;
  fresh: boolean;
}

function isUploadedFileResource(resource: FileResourceReference): resource is UploadedFileResource {
  return (
    'id' in resource &&
    typeof resource.id === 'string' &&
    typeof resource.fileName === 'string' &&
    typeof resource.resourceType === 'string'
  );
}

function getResourceKey(resource?: Nullable<FileResourceReference>): string {
  if (!resource) {
    return '';
  }

  const id = isUploadedFileResource(resource) ? resource.id : '';
  return JSON.stringify([id, resource.relativePath, resource.fileName ?? '', resource.resourceType ?? '']);
}

function createFileState(resource?: Nullable<FileResourceReference>): FileState {
  return {
    sourceKey: getResourceKey(resource),
    resource,
    file: null,
    localFileUrl: null,
    loading: Boolean(resource?.relativePath),
    replaced: false,
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

interface LoadResourceIntoStateOptions {
  resource: FileResourceReference;
  sourceKey: string;
  generation: number;
  signal: AbortSignal;
  getCurrentGeneration: () => number;
  setStoredState: Dispatch<SetStateAction<FileState>>;
  resolveFailureState: (current: FileState) => FileState;
}

async function loadResourceIntoState({
  resource,
  sourceKey,
  generation,
  signal,
  getCurrentGeneration,
  setStoredState,
  resolveFailureState,
}: LoadResourceIntoStateOptions): Promise<void> {
  try {
    if (getCurrentGeneration() !== generation) return;
    const { downloadedFile, localFileUrl } = await downloadResource(resource, signal);
    if (getCurrentGeneration() !== generation) return;
    setStoredState({
      sourceKey,
      resource,
      file: downloadedFile,
      localFileUrl,
      loading: false,
      replaced: false,
    });
  } catch (error) {
    if (getCurrentGeneration() !== generation || isAbortError(error)) return;
    setStoredState(resolveFailureState);
  }
}

const useFile = (initialResource?: Nullable<FileResourceReference>) => {
  const sourceKey = getResourceKey(initialResource);
  const [stableResource, setStableResource] = useState({ sourceKey, resource: initialResource });
  if (stableResource.sourceKey !== sourceKey) {
    setStableResource({ sourceKey, resource: initialResource });
  }
  const resource = stableResource.resource;
  const [storedState, setStoredState] = useState<FileState>(() => createFileState(resource));
  const state = storedState.sourceKey === sourceKey ? storedState : createFileState(resource);
  const operationGeneration = useRef(0);
  const downloadController = useRef<AbortController | null>(null);

  const beginOperation = (): number => {
    operationGeneration.current += 1;
    downloadController.current?.abort();
    downloadController.current = null;
    return operationGeneration.current;
  };

  useEffect(() => {
    operationGeneration.current += 1;
    downloadController.current?.abort();
    downloadController.current = null;
    const generation = operationGeneration.current;
    if (!resource?.relativePath) {
      setStoredState(createFileState(resource));
      return () => {
        operationGeneration.current += 1;
      };
    }

    const controller = new AbortController();
    downloadController.current = controller;
    setStoredState(createFileState(resource));
    void loadResourceIntoState({
      resource,
      sourceKey,
      generation,
      signal: controller.signal,
      getCurrentGeneration: () => operationGeneration.current,
      setStoredState,
      resolveFailureState: current =>
        current.sourceKey === sourceKey ? { ...current, loading: false } : createFileState(resource),
    });

    return () => {
      controller.abort();
      if (downloadController.current === controller) downloadController.current = null;
      operationGeneration.current += 1;
    };
  }, [resource, sourceKey]);

  const setFileData = async (newFile: File): Promise<Nullable<string>> => {
    const generation = beginOperation();
    setStoredState(current =>
      current.sourceKey === sourceKey ? { ...current, loading: true } : { ...createFileState(resource), loading: true },
    );
    return Promise.resolve(newFile.type.startsWith('image/') ? convertToWebp(newFile) : newFile)
      .then(async processedFile => ({ processedFile, localFileUrl: await getLocalFileUrl(processedFile) }))
      .then(({ processedFile, localFileUrl }) => {
        if (operationGeneration.current !== generation) return null;
        setStoredState(current =>
          current.sourceKey === sourceKey
            ? {
                ...current,
                file: processedFile,
                localFileUrl,
                loading: false,
                replaced: true,
              }
            : current,
        );
        return localFileUrl;
      })
      .catch((error: unknown) => {
        if (operationGeneration.current !== generation) return null;
        console.error('Failed to process selected file', error);
        return null;
      })
      .finally(() => {
        if (operationGeneration.current === generation) {
          setStoredState(current => (current.sourceKey === sourceKey ? { ...current, loading: false } : current));
        }
      });
  };

  const uploadFileWithProvenance = async (fileToUpload?: File | null): Promise<FileUploadResult> => {
    const fileToPersist = fileToUpload ?? state.file;
    if (!(state.replaced || fileToUpload) && state.resource && isUploadedFileResource(state.resource)) {
      return { resource: state.resource, fresh: false };
    }

    if (!fileToPersist) {
      throw new Error('No file');
    }
    const generation = operationGeneration.current;
    const uploadedResource = await uploadFile(fileToPersist);
    setStoredState(current =>
      current.sourceKey === sourceKey && operationGeneration.current === generation
        ? { ...current, resource: uploadedResource, replaced: false }
        : current,
    );

    return { resource: uploadedResource, fresh: true };
  };

  const uploadFileData = async (fileToUpload?: File | null): Promise<UploadedFileResource> => {
    const result = await uploadFileWithProvenance(fileToUpload);
    return result.resource;
  };

  const invalidateUploadedResource = (resourceId: string) => {
    setStoredState(current => {
      if (
        current.sourceKey !== sourceKey ||
        !current.resource ||
        !isUploadedFileResource(current.resource) ||
        current.resource.id !== resourceId
      ) {
        return current;
      }

      return {
        ...current,
        resource,
        replaced: Boolean(current.file),
      };
    });
  };

  const resetFile = () => {
    beginOperation();
    setStoredState({
      sourceKey,
      resource,
      file: null,
      localFileUrl: null,
      loading: false,
      replaced: false,
    });
  };
  const restoreFile = async () => {
    if (!resource?.relativePath) {
      resetFile();
      return;
    }
    const generation = beginOperation();
    const controller = new AbortController();
    downloadController.current = controller;
    setStoredState(current =>
      current.sourceKey === sourceKey ? { ...current, loading: true } : { ...createFileState(resource), loading: true },
    );
    await loadResourceIntoState({
      resource,
      sourceKey,
      generation,
      signal: controller.signal,
      getCurrentGeneration: () => operationGeneration.current,
      setStoredState,
      resolveFailureState: current => (current.sourceKey === sourceKey ? { ...current, loading: false } : current),
    }).finally(() => {
      if (downloadController.current === controller) downloadController.current = null;
      if (operationGeneration.current === generation) {
        setStoredState(current => (current.sourceKey === sourceKey ? { ...current, loading: false } : current));
      }
    });
  };
  return {
    localFileUrl: state.localFileUrl,
    loading: state.loading,
    setFileData,
    uploadFileData,
    uploadFileWithProvenance,
    invalidateUploadedResource,
    resetFile,
    restoreFile,
  };
};

export { useFile };
